import React, { useState, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LockIcon, CalendarIcon, ClockIcon, BuildingIcon, UserIcon, MailIcon, PhoneIcon, MapPinIcon, DownloadIcon } from 'lucide-react';
import { FormSection, TextAreaField, FileUploadField, DisplayField } from '../components/FormComponents';
import { mockSupplierRFPs, mockSuppliers } from '../data/mockData';
export function RFPSubmission() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const rfp = mockSupplierRFPs.find(r => r.id === id);
  // Simulated logged-in supplier
  const supplier = mockSuppliers[0];
  const [formData, setFormData] = useState({
    description: ''
  });
  if (!rfp) {
    return <div className="max-w-4xl mx-auto py-12 text-center" data-id="element-2254">
        <LockIcon className="mx-auto h-12 w-12 text-gray-300 mb-3" data-id="element-2255" />
        <h2 className="text-lg font-medium text-gray-900" data-id="element-2256">RFP not found</h2>
        <p className="text-sm text-gray-500 mt-1" data-id="element-2257">
          The RFP invitation you are looking for does not exist.
        </p>
        <button onClick={() => navigate('/tender-board')} className="mt-4 text-indigo-600 hover:text-indigo-500 text-sm font-medium" data-id="element-2258">
          ← Back to Tender Board
        </button>
      </div>;
  }
  const getRFPStatusColor = (status: string) => {
    switch (status) {
      case 'Pending Submission':
        return 'bg-yellow-100 text-yellow-800';
      case 'Submitted':
        return 'bg-blue-100 text-blue-800';
      case 'Under Evaluation':
        return 'bg-indigo-100 text-indigo-800';
      case 'Awarded':
        return 'bg-green-100 text-green-800';
      case 'Not Selected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const isSubmittable = rfp.status === 'Pending Submission';
  const handleSubmit = () => {
    if (!formData.description.trim()) {
      alert('Please provide a description for your RFP proposal.');
      return;
    }
    alert(`RFP proposal submitted successfully for ${rfp.rfpRefNo}!`);
    navigate('/tender-board');
  };
  return <div className="space-y-6 max-w-4xl mx-auto" data-id="element-2259">
      {/* Header */}
      <div className="flex items-center gap-3" data-id="element-2260">
        <div className="p-3 bg-indigo-100 rounded-lg" data-id="element-2261">
          <LockIcon className="h-6 w-6 text-indigo-600" data-id="element-2262" />
        </div>
        <div data-id="element-2263">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2264">
            RFP Proposal Submission
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2265">
            Submit your proposal for{' '}
            <span className="font-medium text-gray-700" data-id="element-2266">{rfp.rfpRefNo}</span>
          </p>
        </div>
      </div>

      {/* RFP Details Card */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6" data-id="element-2267">
        <div className="flex items-center justify-between mb-4" data-id="element-2268">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-2269">RFP Details</h2>
          <div className="flex items-center gap-2" data-id="element-2270">
            <button type="button" onClick={() => alert(`Downloading RFP document for ${rfp.rfpRefNo}...`)} className="inline-flex items-center px-3 py-1.5 border border-indigo-300 shadow-sm text-xs font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100" data-id="element-2271">
              <DownloadIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-2272" />
              Download RFP
            </button>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getRFPStatusColor(rfp.status)}`} data-id="element-2273">
              {rfp.status}
            </span>
          </div>
        </div>

        <h3 className="text-base font-semibold text-gray-900 mb-2" data-id="element-2274">
          {rfp.title}
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-2275">{rfp.description}</p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-2276">
          <div className="flex items-start gap-2" data-id="element-2277">
            <CalendarIcon className="h-4 w-4 text-slate-400 mt-0.5" data-id="element-2278" />
            <div data-id="element-2279">
              <p className="text-xs text-slate-500" data-id="element-2280">Invitation Date</p>
              <p className="text-sm font-medium text-gray-900" data-id="element-2281">
                {rfp.invitationDate}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2" data-id="element-2282">
            <ClockIcon className="h-4 w-4 text-indigo-400 mt-0.5" data-id="element-2283" />
            <div data-id="element-2284">
              <p className="text-xs text-slate-500" data-id="element-2285">Submission Deadline</p>
              <p className="text-sm font-medium text-indigo-700" data-id="element-2286">
                {rfp.submissionDeadline}
              </p>
            </div>
          </div>
          {rfp.submittedDate && <div className="flex items-start gap-2" data-id="element-2287">
              <CalendarIcon className="h-4 w-4 text-green-400 mt-0.5" data-id="element-2288" />
              <div data-id="element-2289">
                <p className="text-xs text-slate-500" data-id="element-2290">Submitted On</p>
                <p className="text-sm font-medium text-green-700" data-id="element-2291">
                  {rfp.submittedDate}
                </p>
              </div>
            </div>}
        </div>
      </div>

      {/* Supplier Details Card */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6" data-id="element-2292">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-2293">
          Supplier Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2294">
          <div className="flex items-center gap-3" data-id="element-2295">
            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-2296">
              <BuildingIcon className="h-5 w-5 text-indigo-600" data-id="element-2297" />
            </div>
            <div data-id="element-2298">
              <p className="text-sm font-medium text-gray-900" data-id="element-2299">
                {supplier.name}
              </p>
              <p className="text-xs text-gray-500" data-id="element-2300">{supplier.code}</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-2301">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-2302">
              <UserIcon className="h-5 w-5 text-gray-600" data-id="element-2303" />
            </div>
            <div data-id="element-2304">
              <p className="text-sm font-medium text-gray-900" data-id="element-2305">
                {supplier.contactPersonName}
              </p>
              <p className="text-xs text-gray-500" data-id="element-2306">Contact Person</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-2307">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-2308">
              <MailIcon className="h-5 w-5 text-gray-600" data-id="element-2309" />
            </div>
            <div data-id="element-2310">
              <p className="text-sm font-medium text-gray-900" data-id="element-2311">
                {supplier.email}
              </p>
              <p className="text-xs text-gray-500" data-id="element-2312">Email</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-2313">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-2314">
              <PhoneIcon className="h-5 w-5 text-gray-600" data-id="element-2315" />
            </div>
            <div data-id="element-2316">
              <p className="text-sm font-medium text-gray-900" data-id="element-2317">
                {supplier.mobile}
              </p>
              <p className="text-xs text-gray-500" data-id="element-2318">Phone</p>
            </div>
          </div>
          <div className="flex items-center gap-3 md:col-span-2" data-id="element-2319">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-2320">
              <MapPinIcon className="h-5 w-5 text-gray-600" data-id="element-2321" />
            </div>
            <div data-id="element-2322">
              <p className="text-sm font-medium text-gray-900" data-id="element-2323">
                {supplier.communicationAddress}
              </p>
              <p className="text-xs text-gray-500" data-id="element-2324">Address</p>
            </div>
          </div>
        </div>
      </div>

      {/* Submission Form */}
      <form className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-2325">
        <FormSection title="Proposal Submission" data-id="element-2326">
          <TextAreaField label="Proposal Description" value={formData.description} onChange={e => setFormData({
          ...formData,
          description: e.target.value
        })} rows={5} placeholder="Describe your proposed solution, methodology, timeline, and pricing approach..." required disabled={!isSubmittable} data-id="element-2327" />
          <FileUploadField label="Proposal Documents" required data-id="element-2328" />
        </FormSection>

        {!isSubmittable && <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4" data-id="element-2329">
            <p className="text-sm text-yellow-800" data-id="element-2330">
              This RFP has already been submitted or is no longer accepting
              proposals. Current status:{' '}
              <span className="font-medium" data-id="element-2331">{rfp.status}</span>
            </p>
          </div>}

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3" data-id="element-2332">
          <button type="button" onClick={() => navigate('/tender-board')} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2333">
            {isSubmittable ? 'Cancel' : 'Back to Tender Board'}
          </button>
          {isSubmittable && <button type="button" onClick={handleSubmit} className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-2334">
              Submit Proposal
            </button>}
        </div>
      </form>
    </div>;
}